import { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../utils/api';
import { useAuth } from '../hooks/useAuth';
import { format } from 'date-fns';

const STATUS_COLORS = {
  open: { bg: '#E6F1FB', color: '#0C447C' },
  pending: { bg: '#FAEEDA', color: '#633806' },
  resolved: { bg: '#EAF3DE', color: '#27500A' },
  closed: { bg: '#f0f0f0', color: '#555' },
};

const Badge = ({ label, colors }) => (
  <span style={{ padding: '2px 8px', borderRadius: '10px', fontSize: '11px', fontWeight: '500', background: colors.bg, color: colors.color }}>
    {label}
  </span>
);

const getMinDateTime = () => {
  const d = new Date(Date.now() + 5 * 60 * 1000);
  const pad = n => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth()+1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

export default function TicketDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { staff: me } = useAuth();
  const [ticket, setTicket] = useState(null);
  const [messages, setMessages] = useState([]);
  const [auditLog, setAuditLog] = useState([]);
  const [staff, setStaff] = useState([]);
  const [loading, setLoading] = useState(true);
  const [replyBody, setReplyBody] = useState('');
  const [replyTo, setReplyTo] = useState('');
  const [replyCC, setReplyCC] = useState('');
  const [replyBCC, setReplyBCC] = useState('');
  const [showCCBCC, setShowCCBCC] = useState(false);
  const [isInternal, setIsInternal] = useState(false);
  const [sending, setSending] = useState(false);
  const [showSchedule, setShowSchedule] = useState(false);
  const [scheduleTime, setScheduleTime] = useState('');
  const [error, setError] = useState('');

  // Attachments are fetched with the session token in the Authorization header (via `api`) and
  // saved from a blob. The token used to ride in the link's query string, which put it into nginx
  // access logs and browser history.
  const downloadAttachment = async (a) => {
    try {
      const res = await api.get(`/attachments/download/${a.id}`, { responseType: 'blob' });
      const url = URL.createObjectURL(res.data);
      const link = document.createElement('a');
      link.href = url;
      link.download = a.filename || 'attachment';
      document.body.appendChild(link);
      link.click();
      link.remove();
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (err) {
      if (err.response?.status !== 401) alert('Could not download that attachment.');
    }
  };
  const [templates, setTemplates] = useState([]);
  const [attachments, setAttachments] = useState([]);
  const [attachFiles, setAttachFiles] = useState([]);
  const [suggesting, setSuggesting] = useState(false);
  const [improving, setImproving] = useState(false);
  const [showLinkModal, setShowLinkModal] = useState(false);
  const [linkText, setLinkText] = useState('');
  const [linkUrl, setLinkUrl] = useState('');
  const [isMobile, setIsMobile] = useState(() => typeof window !== 'undefined' && window.innerWidth < 768);
  const fileInputRef = useRef(null);
  const textareaRef = useRef(null);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const fetchTicket = async () => {
    try {
      const [ticketRes, staffRes, templatesRes, attachmentsRes] = await Promise.all([
        api.get(`/tickets/${id}`),
        api.get('/staff'),
        api.get('/templates'),
        api.get(`/attachments/${id}`),
      ]);
      setTemplates(templatesRes.data.templates || []);
      setAttachments(attachmentsRes.data.attachments || []);
      setTicket(ticketRes.data.ticket);
      setMessages(ticketRes.data.messages);
      setAuditLog(ticketRes.data.audit);
      setStaff(staffRes.data.staff || []);
      setReplyTo(ticketRes.data.ticket?.patient_email || '');
    } catch (err) {
      setError('Could not load ticket');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchTicket(); }, [id]);

  const updateTicket = async (changes) => {
    try {
      const { data } = await api.patch(`/tickets/${id}`, changes);
      setTicket(data.ticket);
      fetchTicket();
    } catch (err) {
      alert(err.response?.data?.error || 'Update failed');
    }
  };

  const deleteTicket = async () => {
    const typed = window.prompt(
      `This permanently deletes ticket ${ticket.reference} — all its messages, attachments and audit history. This cannot be undone.\n\nType the ticket reference to confirm:`
    );
    if (typed === null) return;
    if (typed.trim() !== ticket.reference) {
      alert('Reference did not match — nothing was deleted.');
      return;
    }
    try {
      await api.delete(`/tickets/${id}`);
      navigate('/');
    } catch (err) {
      alert(err.response?.data?.error || 'Delete failed — nothing was deleted.');
    }
  };

  const cancelScheduled = async (messageId) => {
    if (!window.confirm('Cancel this scheduled message? It will not be sent.')) return;
    try {
      await api.patch(`/tickets/${id}/messages/${messageId}/cancel`);
      fetchTicket();
    } catch (err) {
      alert('Failed to cancel scheduled message');
    }
  };

  // tapGP owns the actual send — cancellation happens on their side and we only
  // reflect what they confirm
  const cancelExternalScheduled = async (messageId) => {
    if (!window.confirm('Ask tapGP to cancel this scheduled reminder? It will not be sent if tapGP confirms in time.')) return;
    try {
      const { data } = await api.post(`/tickets/${id}/messages/${messageId}/cancel-external`);
      if (data.status === 'already_sent') {
        alert('Too late — tapGP had already sent this reminder. It is now shown as sent.');
      }
      fetchTicket();
    } catch (err) {
      alert(err.response?.data?.error || 'Could not reach tapGP to cancel — the reminder is unchanged.');
    }
  };

  const insertLink = () => {
    if (!linkText.trim() || !linkUrl.trim()) return;
    const url = linkUrl.startsWith('http') ? linkUrl : `https://${linkUrl}`;
    const markdown = `[${linkText}](${url})`;
    const textarea = textareaRef.current;
    if (textarea) {
      const start = textarea.selectionStart;
      const end = textarea.selectionEnd;
      const newBody = replyBody.substring(0, start) + markdown + replyBody.substring(end);
      setReplyBody(newBody);
    } else {
      setReplyBody(prev => prev + markdown);
    }
    setShowLinkModal(false);
    setLinkText('');
    setLinkUrl('');
  };

  const sendReply = async (scheduledFor = null) => {
    if (!replyBody.trim()) return;
    if (!replyTo.trim()) return alert('To address is required');
    if (scheduledFor && new Date(scheduledFor) <= new Date()) return alert('Scheduled time must be in the future');
    setSending(true);
    try {
      const formData = new FormData();
      formData.append('body', replyBody);
      formData.append('is_internal', isInternal ? 'true' : '');
      formData.append('reply_to', replyTo);
      formData.append('cc', replyCC);
      formData.append('bcc', replyBCC);
      if (scheduledFor) formData.append('scheduled_for', scheduledFor);
      attachFiles.forEach(f => formData.append('attachments', f));
      await api.post(`/tickets/${id}/messages`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setReplyBody('');
      setAttachFiles([]);
      setReplyCC('');
      setReplyBCC('');
      setShowCCBCC(false);
      setShowSchedule(false);
      setScheduleTime('');
      if (fileInputRef.current) fileInputRef.current.value = '';
      fetchTicket();
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to send');
    } finally {
      setSending(false);
    }
  };

  if (loading) return <div style={{ padding: '40px', textAlign: 'center', color: '#999' }}>Loading ticket...</div>;
  if (error || !ticket) return <div style={{ padding: '40px', textAlign: 'center', color: '#c00' }}>{error || 'Ticket not found'}</div>;

  const sc = STATUS_COLORS[ticket.status] || STATUS_COLORS.open;
  const aibusy = suggesting || improving;

  const inputStyle = { width: '100%', padding: '7px 10px', border: '1px solid #ddd', borderRadius: '6px', fontSize: '13px', boxSizing: 'border-box', fontFamily: 'inherit' };

  const rightPanel = (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>

      {attachments.length > 0 && (
        <div style={{ background: '#fff', border: '1px solid #e8e8e8', borderRadius: '10px', padding: '14px' }}>
          <h4 style={{ fontSize: '11px', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.05em', color: '#999', marginBottom: '10px' }}>Attachments</h4>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
            {attachments.map(a => (
              <button key={a.id} type="button" onClick={() => downloadAttachment(a)}
                style={{ fontSize: '13px', color: '#185FA5', textDecoration: 'none', display: 'flex', alignItems: 'center', gap: '6px', background: 'none', border: 'none', padding: 0, cursor: 'pointer', font: 'inherit', textAlign: 'left' }}>
                {a.filename}
                <span style={{ fontSize: '11px', color: '#999' }}>({Math.round(a.size / 1024)}kb)</span>
              </button>
            ))}
          </div>
        </div>
      )}

      <div style={{ background: '#fff', border: '1px solid #e8e8e8', borderRadius: '10px', padding: '14px' }}>
        <h4 style={{ fontSize: '11px', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.05em', color: '#999', marginBottom: '10px' }}>Patient</h4>
        <p style={{ fontSize: '14px', fontWeight: '500', marginBottom: '2px' }}>{ticket.patient_name || '-'}</p>
        <p style={{ fontSize: '13px', color: '#666' }}>{ticket.patient_email}</p>
      </div>

      <div style={{ background: '#fff', border: '1px solid #e8e8e8', borderRadius: '10px', padding: '14px' }}>
        <h4 style={{ fontSize: '11px', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.05em', color: '#999', marginBottom: '10px' }}>Ticket details</h4>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', fontSize: '13px' }}>
          <div>
            <span style={{ color: '#999', display: 'block', fontSize: '11px' }}>Assigned to</span>
            <select value={ticket.assigned_to || ''} onChange={e => updateTicket({ assigned_to: e.target.value || null })}
              style={{ width: '100%', padding: '5px 8px', border: '1px solid #ddd', borderRadius: '6px', fontSize: '13px', marginTop: '3px' }}>
              <option value="">Unassigned</option>
              {staff.filter(s => s.is_active).map(s => (
                <option key={s.id} value={s.id}>{s.name}</option>
              ))}
            </select>
          </div>
          <div>
            <span style={{ color: '#999', display: 'block', fontSize: '11px' }}>Priority</span>
            <select value={ticket.priority} onChange={e => updateTicket({ priority: e.target.value })}
              style={{ width: '100%', padding: '5px 8px', border: '1px solid #ddd', borderRadius: '6px', fontSize: '13px', marginTop: '3px' }}>
              <option value="low">Low</option>
              <option value="normal">Normal</option>
              <option value="high">High</option>
              <option value="urgent">Urgent</option>
            </select>
          </div>
          <div><span style={{ color: '#999' }}>Created</span><br />{ticket.created_at ? format(new Date(ticket.created_at), 'dd MMM yyyy, HH:mm') : '-'}</div>
          <div><span style={{ color: '#999' }}>Source</span><br />{ticket.source === 'email' ? 'Email' : 'Web form'}</div>
        </div>
        {me?.role === 'admin' && (
          <button onClick={deleteTicket}
            style={{ marginTop: '12px', width: '100%', padding: '7px 12px', background: 'none', border: '1px solid #c00', borderRadius: '6px', color: '#c00', fontSize: '12px', fontWeight: '500', cursor: 'pointer' }}>
            Delete ticket permanently
          </button>
        )}
      </div>

      <div style={{ background: '#fff', border: '1px solid #e8e8e8', borderRadius: '10px', padding: '14px' }}>
        <h4 style={{ fontSize: '11px', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.05em', color: '#999', marginBottom: '10px' }}>Audit trail</h4>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0' }}>
          {auditLog.length === 0 && <p style={{ fontSize: '12px', color: '#bbb' }}>No entries yet</p>}
          {auditLog.map((entry, i) => (
            <div key={entry.id} style={{ fontSize: '12px', padding: '6px 0', borderBottom: i < auditLog.length - 1 ? '1px solid #f5f5f5' : 'none' }}>
              <span style={{ color: '#333' }}>{entry.action.replace(/_/g, ' ')}</span>
              {entry.staff_name && <span style={{ color: '#999' }}> · {entry.staff_name}</span>}
              <br />
              <span style={{ color: '#bbb', fontSize: '11px' }}>
                {entry.created_at ? format(new Date(entry.created_at), 'dd MMM, HH:mm') : ''}
              </span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );

  const mainColumn = (
    <div>
      {/* Link insert modal */}
      {showLinkModal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div style={{ background: '#fff', borderRadius: '12px', padding: '24px', width: '360px', boxShadow: '0 8px 32px rgba(0,0,0,0.18)' }}>
            <h3 style={{ fontSize: '15px', fontWeight: '600', marginBottom: '16px' }}>Insert link</h3>
            <div style={{ marginBottom: '12px' }}>
              <label style={{ display: 'block', fontSize: '12px', color: '#666', marginBottom: '4px' }}>Link text (what the patient sees)</label>
              <input value={linkText} onChange={e => setLinkText(e.target.value)}
                placeholder="e.g. Book your appointment"
                style={{ width: '100%', padding: '8px 10px', border: '1px solid #ddd', borderRadius: '6px', fontSize: '13px', boxSizing: 'border-box' }}
                autoFocus />
            </div>
            <div style={{ marginBottom: '16px' }}>
              <label style={{ display: 'block', fontSize: '12px', color: '#666', marginBottom: '4px' }}>URL</label>
              <input value={linkUrl} onChange={e => setLinkUrl(e.target.value)}
                placeholder="https://..."
                style={{ width: '100%', padding: '8px 10px', border: '1px solid #ddd', borderRadius: '6px', fontSize: '13px', boxSizing: 'border-box' }} />
            </div>
            <div style={{ display: 'flex', gap: '8px' }}>
              <button onClick={insertLink} disabled={!linkText.trim() || !linkUrl.trim()}
                style={{ padding: '8px 16px', background: (!linkText.trim() || !linkUrl.trim()) ? '#ddd' : '#185FA5', color: '#fff', border: 'none', borderRadius: '8px', fontSize: '13px', cursor: (!linkText.trim() || !linkUrl.trim()) ? 'not-allowed' : 'pointer' }}>
                Insert
              </button>
              <button onClick={() => { setShowLinkModal(false); setLinkText(''); setLinkUrl(''); }}
                style={{ padding: '8px 16px', background: '#fff', border: '1px solid #ddd', borderRadius: '8px', fontSize: '13px', cursor: 'pointer' }}>
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      <div style={{ marginBottom: '12px' }}>
        <div style={{ display: 'flex', gap: '8px', marginBottom: '12px' }}>
          <button onClick={() => navigate(-1)} style={{ background: 'none', border: '1px solid #ddd', borderRadius: '6px', padding: '5px 12px', fontSize: '13px', cursor: 'pointer' }}>
            Back
          </button>
          <button onClick={() => navigate('/tickets/new')} style={{ background: '#185FA5', color: '#fff', border: 'none', borderRadius: '6px', padding: '5px 12px', fontSize: '13px', cursor: 'pointer', fontWeight: '500' }}>
            + New ticket
          </button>
        </div>
        <h2 style={{ fontSize: isMobile ? '16px' : '18px', fontWeight: '600', marginBottom: '8px' }}>{ticket.subject}</h2>
        <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap' }}>
          <Badge label={ticket.status} colors={sc} />
          <span style={{ fontSize: '13px', color: '#666' }}>Ref: {ticket.reference}</span>
          <span style={{ fontSize: '13px', color: '#666' }}>·</span>
          <span style={{ fontSize: '13px', color: '#666' }}>{ticket.patient_name || ticket.patient_email}</span>
          {!isMobile && (
            <>
              <span style={{ fontSize: '13px', color: '#666' }}>·</span>
              <span style={{ fontSize: '13px', color: '#666' }}>{ticket.source === 'email' ? 'Via email' : 'Via web form'}</span>
            </>
          )}
        </div>
      </div>

      {/* Failed message warning banner */}
      {messages.some(m => m.send_status === 'failed') && (
        <div style={{ background: '#FCEBEB', border: '1px solid #F09595', borderRadius: '8px', padding: '12px 16px', marginBottom: '12px', display: 'flex', alignItems: 'center', gap: '10px' }}>
          <span style={{ fontSize: '18px' }}>⚠️</span>
          <div>
            <div style={{ fontSize: '13px', fontWeight: '600', color: '#791F1F' }}>Scheduled message failed to send</div>
            <div style={{ fontSize: '12px', color: '#9B2C2C', marginTop: '2px' }}>One or more scheduled emails could not be delivered. Please check the messages below and resend manually if needed.</div>
          </div>
        </div>
      )}

      {/* Messages thread */}
      <div style={{ background: '#fff', border: '1px solid #e8e8e8', borderRadius: '10px', overflow: 'hidden', marginBottom: '12px' }}>
        {messages.map((m, i) => (
          <div key={m.id} style={{
            padding: isMobile ? '12px' : '16px',
            borderBottom: i < messages.length - 1 ? '1px solid #f0f0f0' : 'none',
            background: m.is_internal ? '#FFFBF0' : m.send_status === 'scheduled' ? '#F5F0FF' : m.send_status === 'cancelled' ? '#f9f9f9' : '#fff',
            borderLeft: m.is_internal ? '3px solid #EF9F27' : m.send_status === 'scheduled' ? '3px solid #6B21A8' : m.send_status === 'cancelled' ? '3px solid #ccc' : '3px solid transparent',
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px', gap: '8px' }}>
              <div style={{ display: 'flex', gap: '8px', alignItems: 'center', minWidth: 0, flexWrap: 'wrap' }}>
                <div style={{ width: '28px', height: '28px', borderRadius: '50%', background: m.staff_id ? '#CECBF6' : '#E6F1FB', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '10px', fontWeight: '600', color: m.staff_id ? '#3C3489' : '#0C447C', flexShrink: 0 }}>
                  {(m.from_name || m.from_email || '?').slice(0, 2).toUpperCase()}
                </div>
                <div style={{ minWidth: 0, display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap' }}>
                  <span style={{ fontSize: '13px', fontWeight: '500' }}>{m.from_name || m.from_email}</span>
                  {m.is_internal && <span style={{ fontSize: '11px', background: '#FAEEDA', color: '#633806', padding: '1px 6px', borderRadius: '6px', fontWeight: '500' }}>Internal note</span>}
                  {m.send_status === 'scheduled' && (
                    <>
                      <span style={{ fontSize: '11px', background: '#EDE9FE', color: '#6B21A8', padding: '1px 6px', borderRadius: '6px', fontWeight: '500' }}>
                        {m.send_managed_by === 'tapgp' ? 'Scheduled · tapGP Mental Health' : 'Scheduled'} {m.scheduled_for ? format(new Date(m.scheduled_for), 'dd MMM, HH:mm') : ''}
                      </span>
                      <button onClick={() => m.send_managed_by === 'tapgp' ? cancelExternalScheduled(m.id) : cancelScheduled(m.id)}
                        style={{ fontSize: '11px', background: 'none', border: '1px solid #c00', borderRadius: '4px', color: '#c00', padding: '1px 7px', cursor: 'pointer', fontWeight: '500' }}>
                        Cancel send
                      </button>
                    </>
                  )}
                  {m.send_status === 'cancelled' && <span style={{ fontSize: '11px', background: '#f0f0f0', color: '#888', padding: '1px 6px', borderRadius: '6px', fontWeight: '500' }}>Cancelled</span>}
                  {m.send_status === 'failed' && <span style={{ fontSize: '11px', background: '#FCEBEB', color: '#791F1F', padding: '1px 6px', borderRadius: '6px', fontWeight: '500' }}>Send failed</span>}
                </div>
              </div>
              <span style={{ fontSize: '12px', color: '#999', flexShrink: 0, whiteSpace: 'nowrap' }}>
                {format(new Date(m.send_status === 'sent' && m.scheduled_for ? m.scheduled_for : m.created_at), isMobile ? 'dd MMM, HH:mm' : 'dd MMM yyyy, HH:mm')}
              </span>
            </div>
            <div style={{ fontSize: '14px', lineHeight: '1.7', color: m.send_status === 'cancelled' ? '#aaa' : '#333', wordBreak: 'break-word', overflowX: 'auto' }}
              dangerouslySetInnerHTML={{ __html: m.body && m.body.includes('<')
                ? m.body.replace(/<style[^>]*>[\s\S]*?<\/style>/gi, '').replace(/<script[^>]*>[\s\S]*?<\/script>/gi, '')
                : (m.body || '').replace(/\n/g, '<br/>')
              }}
            />
            {m.attachments && m.attachments.length > 0 && (
              <div style={{ marginTop: '8px', display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                {m.attachments.map(a => (
                  <button key={a.id} type="button" onClick={() => downloadAttachment(a)}
                    style={{ fontSize: '12px', color: '#185FA5', textDecoration: 'none', background: '#f0f7ff', padding: '3px 8px', borderRadius: '4px', border: '1px solid #cce0ff', cursor: 'pointer', font: 'inherit' }}>
                    {a.filename} ({Math.round(a.size / 1024)}kb)
                  </button>
                ))}
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Template picker */}
      {templates.length > 0 && !isInternal && (
        <div style={{ marginBottom: '10px' }}>
          <select onChange={e => {
            const t = templates.find(t => t.id === e.target.value);
            if (t) setReplyBody(t.body.replace(/\{\{patient_name\}\}/g, ticket.patient_name?.split(' ')[0] || 'there'));
            e.target.value = '';
          }} style={{ width: '100%', padding: '8px 10px', border: '1px solid #ddd', borderRadius: '8px', fontSize: '13px' }}>
            <option value="">Insert a template...</option>
            {templates.map(t => (
              <option key={t.id} value={t.id}>{t.name}</option>
            ))}
          </select>
        </div>
      )}

      {/* Reply box */}
      <div style={{ background: '#fff', border: '1px solid #e8e8e8', borderRadius: '10px', padding: isMobile ? '12px' : '16px' }}>
        <div style={{ display: 'flex', gap: '8px', marginBottom: '12px' }}>
          <button onClick={() => setIsInternal(false)}
            style={{ padding: '5px 12px', fontSize: '13px', borderRadius: '6px', border: '1px solid #ddd', cursor: 'pointer', background: !isInternal ? '#185FA5' : '#fff', color: !isInternal ? '#fff' : '#333', fontWeight: !isInternal ? '500' : '400' }}>
            Reply to patient
          </button>
          <button onClick={() => setIsInternal(true)}
            style={{ padding: '5px 12px', fontSize: '13px', borderRadius: '6px', border: '1px solid #ddd', cursor: 'pointer', background: isInternal ? '#BA7517' : '#fff', color: isInternal ? '#fff' : '#333', fontWeight: isInternal ? '500' : '400' }}>
            Internal note
          </button>
        </div>

        {!isInternal && (
          <div style={{ marginBottom: '10px', display: 'flex', flexDirection: 'column', gap: '6px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <span style={{ fontSize: '12px', color: '#666', width: '32px', flexShrink: 0 }}>To</span>
              <input value={replyTo} onChange={e => setReplyTo(e.target.value)}
                placeholder="recipient@email.com, another@email.com"
                style={inputStyle} />
              {!showCCBCC && (
                <button onClick={() => setShowCCBCC(true)}
                  style={{ background: 'none', border: 'none', fontSize: '12px', color: '#185FA5', cursor: 'pointer', flexShrink: 0 }}>
                  CC/BCC
                </button>
              )}
            </div>
            {showCCBCC && (
              <>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <span style={{ fontSize: '12px', color: '#666', width: '32px', flexShrink: 0 }}>CC</span>
                  <input value={replyCC} onChange={e => setReplyCC(e.target.value)}
                    placeholder="cc@email.com, another@email.com"
                    style={inputStyle} />
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <span style={{ fontSize: '12px', color: '#666', width: '32px', flexShrink: 0 }}>BCC</span>
                  <input value={replyBCC} onChange={e => setReplyBCC(e.target.value)}
                    placeholder="bcc@email.com"
                    style={inputStyle} />
                </div>
              </>
            )}
          </div>
        )}

        {/* Insert link button */}
        {!isInternal && (
          <div style={{ marginBottom: '6px' }}>
            <button onClick={() => setShowLinkModal(true)}
              style={{ padding: '4px 10px', background: 'none', border: '1px solid #ddd', borderRadius: '6px', fontSize: '12px', color: '#555', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
              🔗 Insert link
            </button>
          </div>
        )}

        <textarea
          ref={textareaRef}
          value={replyBody}
          onChange={e => setReplyBody(e.target.value)}
          placeholder={isInternal ? 'Add a private note - not visible to the patient...' : 'Type your reply to the patient...'}
          rows={4}
          style={{ width: '100%', padding: '10px 12px', border: '1px solid #ddd', borderRadius: '8px', fontSize: '14px', fontFamily: 'inherit', resize: 'vertical', boxSizing: 'border-box', background: isInternal ? '#FFFBF0' : '#fff' }}
        />
        <div style={{ marginTop: '8px' }}>
          <input type="file" multiple ref={fileInputRef} onChange={e => setAttachFiles(prev => [...prev, ...Array.from(e.target.files)])}
            style={{ fontSize: '13px' }} />
          {attachFiles.length > 0 && (
            <div style={{ fontSize: '12px', color: '#666', marginTop: '4px', display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
              {attachFiles.map((f, i) => (
                <span key={i} style={{ background: '#f0f0f0', padding: '2px 8px', borderRadius: '4px', display: 'flex', alignItems: 'center', gap: '4px' }}>
                  {f.name}
                  <button onClick={() => setAttachFiles(prev => prev.filter((_, idx) => idx !== i))}
                    style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#c00', fontWeight: 'bold', fontSize: '14px', lineHeight: 1 }}>x</button>
                </span>
              ))}
            </div>
          )}
        </div>

        {showSchedule && !isInternal && (
          <div style={{ marginTop: '10px', display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap', padding: '10px', background: '#F5F0FF', borderRadius: '8px', border: '1px solid #DDD6FE' }}>
            <span style={{ fontSize: '13px', color: '#6B21A8', fontWeight: '500' }}>Send at:</span>
            <input type="datetime-local" value={scheduleTime} onChange={e => setScheduleTime(e.target.value)}
              min={getMinDateTime()}
              style={{ padding: '6px 10px', border: '1px solid #DDD6FE', borderRadius: '6px', fontSize: '13px' }} />
            <button onClick={() => { setShowSchedule(false); setScheduleTime(''); }}
              style={{ background: 'none', border: 'none', fontSize: '12px', color: '#888', cursor: 'pointer' }}>
              Cancel
            </button>
          <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap', marginTop: '8px' }}>
            {[{ label: 'In 12 days', days: 12 }, { label: 'In 26 days', days: 26 }, { label: 'In 3 weeks', days: 21 }].map(({ label, days }) => {
              const d = new Date(); d.setDate(d.getDate() + days); d.setHours(10, 0, 0, 0);
              const pad = n => String(n).padStart(2, '0');
              const val = `${d.getFullYear()}-${pad(d.getMonth()+1)}-${pad(d.getDate())}T10:00`;
              return (
                <button key={label} onClick={() => setScheduleTime(val)}
                  style={{ padding: '4px 10px', background: scheduleTime === val ? '#6B21A8' : '#fff', color: scheduleTime === val ? '#fff' : '#6B21A8', border: '1px solid #6B21A8', borderRadius: '6px', fontSize: '12px', cursor: 'pointer' }}>
                  {label}
                </button>
              );
            })}
          </div>
        </div>
        )}

        <div style={{ display: 'flex', gap: '8px', marginTop: '10px', alignItems: 'center', flexWrap: 'wrap' }}>
          <button onClick={async () => {
            setSuggesting(true);
            try {
              const { data } = await api.post(`/tickets/${id}/suggest-reply`);
              setReplyBody(data.suggestion);
            } catch (err) {
              alert('Failed to generate suggestion');
            } finally {
              setSuggesting(false);
            }
          }} disabled={aibusy || isInternal}
            style={{ padding: '8px 16px', background: aibusy ? '#ddd' : '#6B21A8', color: '#fff', border: 'none', borderRadius: '8px', fontSize: '13px', fontWeight: '500', cursor: aibusy ? 'not-allowed' : 'pointer' }}>
            {suggesting ? 'Thinking...' : 'AI suggest'}
          </button>

          <button onClick={async () => {
            if (!replyBody.trim()) return;
            setImproving(true);
            try {
              const { data } = await api.post(`/tickets/${id}/improve-reply`, { draft: replyBody });
              setReplyBody(data.improved);
            } catch (err) {
              alert('Failed to improve reply');
            } finally {
              setImproving(false);
            }
          }} disabled={aibusy || isInternal || !replyBody.trim()}
            style={{ padding: '8px 16px', background: (aibusy || !replyBody.trim()) ? '#ddd' : '#0F766E', color: '#fff', border: 'none', borderRadius: '8px', fontSize: '13px', fontWeight: '500', cursor: (aibusy || !replyBody.trim()) ? 'not-allowed' : 'pointer' }}>
            {improving ? 'Improving...' : 'AI improve'}
          </button>

          <button onClick={sendReply} disabled={sending || !replyBody.trim()}
            style={{ padding: '8px 16px', background: '#185FA5', color: '#fff', border: 'none', borderRadius: '8px', fontSize: '13px', fontWeight: '500', cursor: sending ? 'not-allowed' : 'pointer', opacity: sending ? 0.7 : 1 }}>
            {sending ? 'Sending...' : isInternal ? 'Add note' : 'Send reply'}
          </button>

          {!isInternal && (
            showSchedule ? (
              <button onClick={() => {
                if (!scheduleTime) return alert('Please pick a date and time');
                sendReply(scheduleTime);
              }} disabled={sending || !replyBody.trim() || !scheduleTime}
                style={{ padding: '8px 16px', background: (sending || !replyBody.trim() || !scheduleTime) ? '#ddd' : '#6B21A8', color: '#fff', border: 'none', borderRadius: '8px', fontSize: '13px', fontWeight: '500', cursor: (sending || !replyBody.trim() || !scheduleTime) ? 'not-allowed' : 'pointer' }}>
                {sending ? 'Scheduling...' : 'Schedule send'}
              </button>
            ) : (
              <button onClick={() => { setShowSchedule(true); if (!scheduleTime) { const d = new Date(); d.setDate(d.getDate() + 1); d.setHours(10, 0, 0, 0); const pad = n => String(n).padStart(2, '0'); setScheduleTime(`${d.getFullYear()}-${pad(d.getMonth()+1)}-${pad(d.getDate())}T10:00`); } }} disabled={!replyBody.trim()}
                style={{ padding: '8px 16px', background: 'none', border: '1px solid #ddd', borderRadius: '8px', fontSize: '13px', color: '#555', cursor: !replyBody.trim() ? 'not-allowed' : 'pointer', opacity: !replyBody.trim() ? 0.5 : 1 }}>
                Send later
              </button>
            )
          )}

          <select onChange={e => e.target.value && updateTicket({ status: e.target.value })} value={ticket.status}
            style={{ padding: '7px 10px', border: '1px solid #ddd', borderRadius: '8px', fontSize: '13px' }}>
            <option value="open">Open</option>
            <option value="pending">Pending</option>
            <option value="resolved">Resolved</option>
            <option value="closed">Closed</option>
          </select>
        </div>
      </div>
    </div>
  );

  return isMobile ? (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
      {mainColumn}
      {rightPanel}
    </div>
  ) : (
    <div style={{ display: 'grid', gridTemplateColumns: '1fr 260px', gap: '16px', alignItems: 'start' }}>
      {mainColumn}
      {rightPanel}
    </div>
  );
}
